import { DialogPrivacyPolicyUserComponent } from './components/dialog-privacy-policy-user/dialog-privacy-policy-user.component';
import { UserAdminContactTabComponent } from './components/user-admin-contact-tab/user-admin-contact-tab.component';
import { UserAdminContractTabComponent } from './components/user-admin-contract-tab/user-admin-contract-tab.component';
import { UserAdminProductTabComponent } from './components/user-admin-product-tab/user-admin-product-tab.component';
import { UserAdminUsersTabComponent } from './components/user-admin-users-tab/user-admin-users-tab.component';
import {
  UserAdminWarningTabComponent,
  WarnThresold,
} from './components/user-admin-warning-tab/user-admin-warning-tab.component';

import { AuthService } from '../auth/auth.service';
import { UserService } from './services/user.service';

import { IsNilPipe } from '../pipes/is-nil.pipe';

import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';

import { TranslateModule } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { contactSubjects } from './constants/constants';
import { ExpansionAction } from './types/expansion-action.type';
import { UserSection } from './types/user-section.type';
import moment from 'moment';
import { Moment } from 'moment';
import { isNil } from 'src/utils/utils';

export const MODULES = [
  CommonModule,
  MatTabsModule,
  MatButtonModule,
  TranslateModule,
];
export const COMPONENTS = [
  FaIconComponent,
  UserAdminUsersTabComponent,
  UserAdminProductTabComponent,
  UserAdminContractTabComponent,
  UserAdminContactTabComponent,
  UserAdminWarningTabComponent,
];

export const PIPES = [IsNilPipe];

@Component({
  selector: 'app-user-admin',
  templateUrl: './user-admin.component.html',
  styleUrls: ['./user-admin.component.scss'],
  providers: [],
  imports: [...MODULES, ...COMPONENTS, ...PIPES],
})
export class UserAdminComponent implements OnInit {
  private auth = inject(AuthService);
  public userService = inject(UserService);
  public dialog = inject(MatDialog);
  private _formBuilder = inject(UntypedFormBuilder);
  private destroyRef = inject(DestroyRef);

  contactSubjects = contactSubjects;

  showRegionEditInfo = signal(false);
  showContactEditInfo = signal(false);
  contactFormWasSent = signal(false);
  privacyPolicyAccepted = signal(false);
  buttonSelected = signal({
    expandAll: {
      online: false,
      app: false,
      delivery: false,
    },
    collapseAll: {
      online: false,
      app: false,
      delivery: false,
    },
  });

  productViewUserData = signal({});

  contractStartDtg = signal<Moment>(undefined);
  contractExpiryDtg = signal<Moment>(undefined);

  savingWarnThresholds = signal(false);

  customerInfo = signal('');
  customerInfoLines = signal([]);
  warnThresolds = signal<WarnThresold[]>(undefined);
  warnInfo = signal<boolean>(undefined);
  hasSummerWarnings = signal<boolean>(false);

  customerContact = signal('');
  customerContactLines = signal([]);
  userHasAdminPermissions = computed(() => this.auth.hasUserAdminPermissions());

  contingents = signal({});

  ngOnInit(): void {
    this.setHasSummerWarnings();
    this.onCheckDidAcceptPrivacyPolicy();
    if (this.userService.customerDocument().customerId !== '1') {
      this.userService.getUsers();
      this.userService.extractProducts();
      this.setContractTimeRange();
      this.getCustomerMetaData();
      this.getCustomerContact();

      const currentUserName = JSON.parse(
        localStorage.getItem('user_customer_document')
      )['name'];
      this.userService.contactFormGroup = this._formBuilder.group({
        name: new UntypedFormControl('', [Validators.required]),
        email: new UntypedFormControl(currentUserName, [
          Validators.required,
          Validators.email,
        ]),
        phone: new UntypedFormControl('', [
          Validators.required,
          Validators.pattern(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/),
        ]),
        subject: new UntypedFormControl('', [Validators.required]),
        text: new UntypedFormControl('', [Validators.required]),
        privacy: new UntypedFormControl(false, [Validators.requiredTrue]),
      });

      this.getContingents();
    }
    this.listenToProductViewUserDataChanges();
  }

  onCheckDidAcceptPrivacyPolicy(): any {
    if (!this.privacyPolicyAccepted()) {
      const profile = this.auth.getUserProfile();
      if (profile.hasOwnProperty('metadata') && profile['metadata'] !== null) {
        if (
          profile['metadata'].hasOwnProperty('global') &&
          profile['metadata']['global'] !== null
        ) {
          if (
            profile['metadata']['global'].hasOwnProperty(
              'userAdminPrivacyPolicyAccepted'
            )
          ) {
            if (
              profile['metadata']['global'][
                'userAdminPrivacyPolicyAccepted'
              ] === true
            ) {
              this.privacyPolicyAccepted.set(true);
              return;
            }
          }
        }
      }

      // Dialog privacy policy
      const dialogRef = this.dialog.open(DialogPrivacyPolicyUserComponent, {
        data: null,
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result === true) {
          const userProfile = this.auth.getUserProfile();
          if (userProfile) {
            let global = userProfile['metadata']['global'];
            if (!global) {
              global = {};
            }
            global['userAdminPrivacyPolicyAccepted'] = true;
            this.auth.updateUserData('global', global);
            this.privacyPolicyAccepted.set(true);
          }
        }
      });
    }
  }

  onCollapseAll(section: UserSection): void {
    this.setButtonSelected('expandAll', section, false);
    this.setButtonSelected('collapseAll', section, true);
  }

  onExpandAll(section: UserSection): void {
    this.setButtonSelected('expandAll', section, true);
    this.setButtonSelected('collapseAll', section, false);
  }

  onExpandedChanged(section: UserSection): void {
    this.setButtonSelected('expandAll', section, false);
    this.setButtonSelected('collapseAll', section, false);
  }

  onChangeThresholds(thresholds: WarnThresold[]) {
    console.log('onChangeThresholds', thresholds);
    this.savingWarnThresholds.set(true);
    this.warnThresolds.set([...thresholds]);
    this.userService.patchCustomerMetaData('thresholds', thresholds).subscribe(
      data => {
        this.savingWarnThresholds.set(false);
      },
      err => console.error(err)
    );
  }

  onChangeInfo(info: boolean) {
    this.savingWarnThresholds.set(true);
    this.warnInfo.set(info);
    this.userService.patchCustomerMetaData('info', info).subscribe(
      data => {
        this.savingWarnThresholds.set(false);
      },
      err => console.error(err)
    );
  }

  onEditRegion(): void {
    this.userService.regionFormGroup.enable();
    this.showRegionEditInfo.set(true);
  }

  onSaveRegionInfo(): void {
    const formValues = this.userService.regionFormGroup.value;
    const customerId =
      this.userService.customerDocument()?.customerId || 'Unbekannt';

    // Holt das Array direkt aus dem Service
    const gmaList = formValues.gmaList || [];

    // 1. Basis-Regionsdaten formatieren
    let formattedInfo =
      `Einsatzort(e):\n${formValues.locations?.trim() || '-'}\n\n` +
      `Lokale Besonderheiten im Winter:\n${formValues.localFeatures?.trim() || '-'}\n\n` +
      `Höhenlage:\n   - Tiefste Lage: ${formValues.elevationMin ?? '-'} m über NN\n   - Höchste Lage: ${formValues.elevationMax ?? '-'} m über NN\n\n` +
      `Brücken im Einsatzgebiet:\n${formValues.bridges || '-'}\n\n` +
      `=== GMA ANLAGEN ===\n` +
      `GMA Vorhanden:\n${formValues.hasGma === 'ja' ? 'JA' : 'NEIN'}\n\n` +
      `=== GMA DETAILS ===\n`;

    if (formValues.hasGma === 'ja' && gmaList.length > 0) {
      gmaList.forEach((gma: any, index: number) => {
        let manufacturerAndType = gma.gmaManufacturer || '-';

        // FIX: Erkennt "Campbell", da der Auswahleffekt jetzt "Campbell; Modell: Wintersense Solar" liefert
        if (gma.gmaManufacturer && gma.gmaManufacturer.startsWith('Campbell')) {
          // Wir extrahieren das Modell oder setzen den Standard-Text
          const modelName = gma.gmaManufacturer.includes('Modell: ')
            ? gma.gmaManufacturer.split('Modell: ')[1]
            : 'Wintersense Solar';

          manufacturerAndType = `Campbell ${modelName}${
            gma.gmaProductKey
              ? ` | Produkt-Key: ${gma.gmaProductKey.trim()}`
              : ''
          }`;
        }

        formattedInfo +=
          `--- Anlage ${index + 1} ---\n` +
          `Geokoordinaten & Höhe:\n${gma.gmaCoordinates?.trim() || '-'}\n\n` +
          `Hersteller & Typ:\n${manufacturerAndType}\n\n` +
          `Webcam:\n${gma.gmaWebcam === 'vorhanden' ? 'Webcam vorhanden' : 'keine Webcam vorhanden'}\n\n`;
      });
    } else {
      formattedInfo += `-`;
    }

    this.userService
      .patchCustomerMetaData('regionInfo', formattedInfo)
      .subscribe({
        next: () => {
          this.customerInfo.set(formattedInfo);
          this.customerInfoLines.set(formattedInfo.split('\n'));
          this.showRegionEditInfo.set(false);

          // Support E-Mail triggern
          const mailSubject = `Meteofocus UserAdmin - Detailliertes GMA-Update (KNr. ${customerId})`;
          const mailMessage = `Es wurden neue GMA-Spezifikationen hinterlegt:\n\n${formattedInfo}`;
          this.userService
            .sendContactForm('UserAdmin System', mailSubject, mailMessage)
            .subscribe();
        },
        error: err => console.error(err),
      });
  }

  onSaveContactInfo(): void {
    const customerId =
      this.userService.customerDocument()?.customerId || 'Unbekannt';

    const rows =
      (this.userService.contactInfoFormGroup.get('contacts') as any).value ||
      [];
    const formValues = this.userService.contactInfoFormGroup.value;

    const formattedContact = rows
      .filter((r: any) => r.name || r.email || r.phone)
      .map(
        (r: any) => `${r.name || '-'} | ${r.email || '-'} | ${r.phone || '-'}`
      )
      .join('\n');

    const company = formValues.billingCompanyName?.trim() || '';
    const street = formValues.billingStreet?.trim() || '';
    const zipCity =
      `${formValues.billingZip?.trim() || ''} ${formValues.billingCity?.trim() || ''}`.trim();

    const addressLines = [company, street, zipCity].filter(line => line !== '');
    const formattedAddress =
      addressLines.length > 0 ? addressLines.join('\n') : '-';

    const fullPayloadString =
      `=== ANSPRECHPARTNER ===\n${formattedContact || '-'}\n\n` +
      `=== RECHNUNGSLEGUNG ===\n` +
      `Postanschrift:\n${formattedAddress}\n\n` +
      `E-Mail Rechnungslegung:\n${formValues.billingEmail || '-'}`;

    this.userService
      .patchCustomerMetaData('contactInfo', fullPayloadString)
      .subscribe({
        next: () => {
          this.customerContact.set(fullPayloadString);
          this.customerContactLines.set(fullPayloadString.split('\n'));
          this.showContactEditInfo.set(false);

          const mailSubject = `Meteofocus UserAdmin - Kontaktdaten & Rechnungs-Update (KNr. ${customerId})`;
          const mailMessage = `Es wurden Kontaktdaten oder Rechnungsdaten aktualisiert.\n\n${fullPayloadString}`;

          this.userService
            .sendContactForm('Meteofocus System', mailSubject, mailMessage)
            .subscribe();
        },
        error: err => console.error(err),
      });
  }

  onSendContactForm(): void {
    const customerId = this.userService.customerDocument().customerId;
    const name = this.userService.contactFormGroup.controls['name'].value;
    const email = this.userService.contactFormGroup.controls['email'].value;
    const phone = this.userService.contactFormGroup.controls['phone'].value;
    const subject = this.userService.contactFormGroup.controls['subject'].value;
    const text = this.userService.contactFormGroup.controls['text'].value;

    const mailSubject =
      'Einsatzwetter UserAdmin Kontaktformular Anfrage von ' +
      customerId +
      ' (' +
      name +
      ')';
    const mailMessage =
      'Mitteilung von ' +
      name +
      ', Kundennummer ' +
      customerId +
      '\n' +
      'Betreff: ' +
      subject +
      '\n\nTelefon:' +
      phone +
      '\nMitteilung:\n' +
      text;

    this.userService.sendContactForm(name, mailSubject, mailMessage).subscribe(
      data => {
        this.contactFormWasSent.set(true);
        setTimeout(() => {
          this.contactFormWasSent.set(false);
        }, 30000);
        this.userService.contactFormGroup.reset();
      },
      error => console.log('Error UserService SendContactForm', error)
    );
  }

  private setContractTimeRange(): void {
    let contractStartDtg = moment().set({ hour: 0, minute: 0, second: 0 });
    let contractExpiryDtg = moment().set({ hour: 0, minute: 0, second: 0 });
    for (const productKey of Object.keys(this.userService.customerDocument())) {
      if (productKey.indexOf('Products') > 0) {
        for (const product of this.userService.customerDocument()[productKey]) {
          if (this.auth.isProductActive(productKey, product.id, true)) {
            const productContractStartDtg = moment(
              product.contractStartDtg + 'T000000'
            );
            if (productContractStartDtg.isBefore(contractStartDtg)) {
              contractStartDtg = productContractStartDtg?.clone();
            }
            if (!isNil(contractExpiryDtg)) {
              if (isNil(product.contractExpiryDtg)) {
                contractExpiryDtg = null;
              } else {
                const productContractExpiryDtg = moment(
                  product.contractExpiryDtg + 'T000000'
                );
                if (productContractExpiryDtg.isAfter(contractExpiryDtg)) {
                  contractExpiryDtg = productContractExpiryDtg?.clone();
                }
              }
            }
          }
        }
      }
    }
    this.contractStartDtg.set(contractStartDtg?.clone());
    this.contractExpiryDtg.set(contractExpiryDtg?.clone());
  }

  private getCustomerMetaData(): void {
    this.userService.getCustomerMetaData().subscribe({
      next: data => {
        // 1. Sicherstellen, dass data ein gültiges Objekt ist
        if (!data) return;

        // 2. Kontingente sicher prüfen und setzen
        if ('contingent' in data && data['contingent']) {
          this.contingents.set({ ...data['contingent'] });
        }

        // 3. RegionInfo sicher prüfen und setzen
        if ('regionInfo' in data && data['regionInfo']) {
          this.customerInfo.set(data['regionInfo']);
          this.customerInfoLines.set([...this.customerInfo().split('\n')]);
        }

        // 4. FIX: Prüfen, ob 'thresholds' existiert UND tatsächlich iterierbar (ein Array) ist
        if ('thresholds' in data && Array.isArray(data['thresholds'])) {
          this.warnThresolds.set([...data['thresholds']]);
        } else if ('thresholds' in data && data['thresholds']) {
          // Fallback: Falls es ein einzelnes Objekt statt eines Arrays ist, packen wir es in ein Array
          this.warnThresolds.set([data['thresholds']]);
        } else {
          // Fallback: Wenn es leer/null ist, setzen wir ein leeres Array
          this.warnThresolds.set([]);
        }

        // 5. Info sicher prüfen und setzen
        if ('info' in data && data['info']) {
          this.warnInfo.set(data['info']);
        }
      },
      error: err => console.error(err),
    });
  }

  private getCustomerContact(): void {
    this.userService.getCustomerMetaData().subscribe({
      next: metadata => {
        const contactInfoString = metadata?.contactInfo || '';
        this.customerContact.set(contactInfoString);
        this.customerContactLines.set([...contactInfoString.split('\n')]);
      },
      error: err => console.error(err),
    });
  }

  private getContingents(): void {
    this.userService.getUserContingents().subscribe(
      data => {
        this.contingents.set({ ...data });
      },
      err => console.error(err)
    );
  }

  private setButtonSelected(
    action: ExpansionAction,
    property: UserSection,
    value: boolean
  ) {
    this.buttonSelected.update(buttonSelected => ({
      ...buttonSelected,
      [action]: {
        ...buttonSelected[action],
        [property]: value,
      },
    }));
  }

  private setHasSummerWarnings() {
    this.hasSummerWarnings.set(
      this.auth.isProductActive('egwSummerProducts', null, false, true)
    );
  }

  private listenToProductViewUserDataChanges() {
    this.userService
      .getProductViewUserData()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(item => this.productViewUserData.set({ ...item }));
  }
}
